import type { ComponentChildren } from "preact";
import { clearAuthToken } from "../api";

type ActiveSection = "conversas" | "clientes" | "analise";

interface AppShellProps {
  active: ActiveSection;
  title: string;
  subtitle?: string | null;
  mock: boolean;
  children: ComponentChildren;
  inspector?: ComponentChildren;
  headerInfo?: ComponentChildren;
  headerExtra?: ComponentChildren;
  bodyTop?: ComponentChildren;
}

const NAV: Array<{ key: ActiveSection; label: string; href: string }> = [
  { key: "conversas", label: "Conversas", href: "/conversas" },
  { key: "clientes", label: "Clientes", href: "/clientes" },
  { key: "analise", label: "Análise", href: "/analise" },
];

function NavIcon({ name }: { name: ActiveSection | "logout" }) {
  if (name === "clientes") {
    return (
      <svg class="sidebar-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M16 11a4 4 0 1 0-3.2-6.4A4 4 0 0 1 16 11Z" />
        <path d="M7.5 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
        <path d="M2 20a5.5 5.5 0 0 1 11 0v1H2v-1Z" />
        <path d="M14 21v-1a7 7 0 0 0-2.2-5.1A5 5 0 0 1 22 20v1h-8Z" />
      </svg>
    );
  }
  if (name === "analise") {
    return (
      <svg class="sidebar-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 19h16v2H4v-2Z" />
        <path d="M6 11h3v6H6v-6Z" />
        <path d="M11 5h3v12h-3V5Z" />
        <path d="M16 8h3v9h-3V8Z" />
      </svg>
    );
  }
  if (name === "logout") {
    return (
      <svg class="sidebar-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 4h8v2H6v12h6v2H4V4Z" />
        <path d="M15.6 7.4 20.2 12l-4.6 4.6-1.4-1.4 2.2-2.2H10v-2h6.4l-2.2-2.2 1.4-1.4Z" />
      </svg>
    );
  }
  return (
    <svg class="sidebar-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5h16v3H4V5Z" />
      <path d="M4 10.5h16v3H4v-3Z" />
      <path d="M4 16h16v3H4v-3Z" />
    </svg>
  );
}

function suffix(mock: boolean): string {
  return mock ? "?mock=1" : "";
}

function logout() {
  clearAuthToken();
  window.location.href = "/login?next=/conversas";
}

export function AppShell(
  { active, title, subtitle, mock, children, inspector, headerInfo, headerExtra, bodyTop }: AppShellProps,
) {
  return (
    <div class="admin-shell">
      <aside class="app-sidebar">
        <div class="sidebar-brand">
          <span class="sidebar-brand-name">WhereInRio</span>
          <span class="sidebar-brand-sub">Admin</span>
        </div>
        <nav class="sidebar-nav" aria-label="Principal">
          {NAV.map((item) => (
            <a
              key={item.key}
              class={`sidebar-link ${active === item.key ? "is-active" : ""}`}
              href={`${item.href}${suffix(mock)}`}
              title={item.label}
            >
              <NavIcon name={item.key} />
              <span class="sidebar-link-label">{item.label}</span>
            </a>
          ))}
        </nav>
        <button class="sidebar-logout" type="button" onClick={logout} aria-label="Logout" title="Logout">
          <NavIcon name="logout" />
          <span class="sidebar-logout-label">Logout</span>
        </button>
      </aside>
      <div class="admin-area">
        <header class="dash-header">
          <div class="dash-header-main">
            <h1 class="dash-title">{title}</h1>
            {subtitle && <p class="dash-subtitle">{subtitle}</p>}
          </div>
          {headerInfo && <div class="dash-header-info">{headerInfo}</div>}
          {headerExtra && <div class="dash-header-extra">{headerExtra}</div>}
        </header>
        <div class={inspector ? "admin-body admin-body--with-inspector" : "admin-body"}>
          {bodyTop && <div class="admin-body-top">{bodyTop}</div>}
          <main class="dashboard-main">
            {children}
          </main>
          {inspector && <aside class="desktop-inspector">{inspector}</aside>}
        </div>
      </div>
    </div>
  );
}
